import { Box, Text } from "@chakra-ui/react";
import { useTranslation } from "../i18nShim";
import { FormInput, FormTextarea } from "./inputs";

export const AddButton = ({ label, onClick }) => (
    <Box
        as="button"
        type="button"
        onClick={onClick}
        w="full"
        py={2.5}
        borderWidth={1}
        borderStyle="dashed"
        borderColor="border.primary"
        borderRadius={8}
        color="text.grey"
        fontSize="sm"
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={2}
        _hover={{ borderColor: "purple.400", color: "purple.400", bg: "bg.highlight" }}
        _active={{ transform: "scale(0.99)" }}
        transition="border-color 0.15s, color 0.15s, background 0.15s, transform 0.1s"
    >
        <Text as="span">+</Text>
        {label}
    </Box>
);

export const RemoveButton = ({ onClick }) => {
    const { t } = useTranslation("jobOffer");
    return (
        <Box
            as="button"
            type="button"
            onClick={onClick}
            w={7}
            h={7}
            borderRadius={6}
            bg="red.900"
            color="red.400"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            fontSize="lg"
            lineHeight={1}
            transition="opacity 0.15s, transform 0.1s"
            _hover={{ opacity: 0.8 }}
            _active={{ transform: "scale(0.9)" }}
            aria-label={t("ui.remove")}
        >
            ×
        </Box>
    );
};

export const ItemHeader = ({ title, index, onRemove, badge }) => (
    <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={4}>
        <Box
            display="flex"
            flexDirection={{ base: 'column', md: 'row' }}
            alignItems={{ base: 'flex-start', md: 'center' }}
            gap={{ base: 1.5, md: 2 }}
        >
            <Box display="flex" alignItems="center" gap={2}>
                <Box
                    w={5}
                    h={5}
                    borderRadius="full"
                    bg="purple.900"
                    color="purple.400"
                    fontSize="xs"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontWeight={500}
                    flexShrink={0}
                >
                    {index + 1}
                </Box>
                <Text fontSize="sm" color="text.grey">{title}</Text>
                {badge && (
                    <Text display={{ base: 'none', md: 'block' }} fontSize="10px" px={2} py={0.5} borderRadius={6} bg="bg.highlight" color="text.grey">
                        {badge}
                    </Text>
                )}
            </Box>
            {badge && (
                <Text display={{ base: 'block', md: 'none' }} ml="28px" fontSize="10px" px={2} py={0.5} borderRadius={6} bg="bg.highlight" color="text.grey">
                    {badge}
                </Text>
            )}
        </Box>
        {onRemove && <RemoveButton onClick={onRemove} />}
    </Box>
);

export const RepeatableText = ({ values, onChange, placeholder, label, multiline, required, invalid, warning }) => {
    const { t } = useTranslation("jobOffer");
    const add = () => onChange([...values, ""]);
    const remove = (i) => onChange(values.filter((_, idx) => idx !== i));
    const update = (i, val) => {
        const updated = [...values];
        updated[i] = val;
        onChange(updated);
    };
    const borderColor = invalid ? "red.500" : warning ? "yellow.500" : undefined;

    return (
        <Box display="flex" flexDirection="column" gap={1.5}>
            <Text fontSize="sm" fontWeight={500} color={invalid ? "red.400" : "text.white"}>
                {label}
                {required && <Text as="span" color={invalid ? "red.400" : "purple.400"} fontWeight={700} ml={1}>*</Text>}
            </Text>
            <Box display="flex" flexDirection="column" gap={2}>
                {values.map((val, i) => (
                    <Box key={i} display="flex" gap={2} alignItems="flex-start">
                        {multiline ? (
                            <FormTextarea value={val} onChange={(e) => update(i, e.target.value)} placeholder={placeholder} rows={2} flex={1} {...(borderColor ? { borderColor } : {})} />
                        ) : (
                            <FormInput value={val} onChange={(e) => update(i, e.target.value)} placeholder={placeholder} flex={1} {...(borderColor ? { borderColor } : {})} />
                        )}
                        <RemoveButton onClick={() => remove(i)} />
                    </Box>
                ))}
                <Box
                    as="button"
                    type="button"
                    onClick={add}
                    alignSelf="flex-start"
                    fontSize="xs"
                    color="purple.400"
                    display="flex"
                    alignItems="center"
                    gap={1}
                    _hover={{ opacity: 0.8 }}
                >
                    <Text as="span">+</Text> {values.length === 0 ? t("ui.addItem") : t("ui.addAnother")}
                </Box>
            </Box>
        </Box>
    );
};
